<script lang="ts" setup>
import { useField } from "vee-validate";
import GenericInput from "~/components/CustomInputs/GenericInput.vue";
import type { InputType } from "~/components/CustomInputs/protocols";

interface Props {
  name: string;
  label?: string;
  placeholder?: string;
  inputType?: InputType;
  theme?: "light" | "dark";
  showReset?: boolean;
  id?: string;
  loading?: boolean;
  labelClass?: string;
  passwordToggle?: boolean;
  customErrorMessage?: string;
  disabled?: boolean;
  checked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  theme: "dark",
  inputType: "default",
  label: undefined,
  placeholder: undefined,
  showReset: false,
  id: undefined,
  loading: false,
  labelClass: undefined,
  customErrorMessage: undefined,
});

const emit = defineEmits<{
  (event: "reset"): void;
  (event: "blur"): void;
}>();

const {
  value,
  errorMessage,
  label: fieldLabel,
  handleBlur: blurValidate,
  meta,
} = useField(
  () => props.name,
  {},
  {
    validateOnMount: false,
  },
);

const label = computed(() => {
  return props.label || toValue(fieldLabel);
});

const placeholder = computed(() => {
  return props.placeholder ?? label.value ?? "";
});

defineOptions({
  inheritAttrs: false,
});

const fieldId = computed(() => props.id ?? `input-${props.name}`);

const onResetInput = () => {
  if (typeof value.value === "string") {
    value.value = "";
  } else if (typeof value.value === "number") {
    value.value = 0;
  }

  emit("reset");
};

const showPassword = ref(false);

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const handleBlur = (e?: Event) => {
  blurValidate(e);

  emit("blur");
};

const inputTypeAttribute = computed(() => {
  if (props.passwordToggle) {
    return showPassword.value ? "text" : "password";
  }

  return "text";
});

const newErrorMessage = computed(
  () => errorMessage.value ?? props.customErrorMessage,
);
</script>

<template>
  <div
    class="flex flex-col text-green-white mb-3"
    :class="{
      'text-green-white': props.theme === 'dark',
      'text-mirage': props.theme === 'light',
    }"
  >
    <label
      v-if="props.label"
      :for="fieldId"
      class="mb-1"
      :class="props.labelClass"
    >
      {{ props.label }}
    </label>
    <div class="relative z-0">
      <GenericInput
        :id="fieldId"
        v-model="value"
        :name="props.name"
        :input-type="props.inputType"
        class="text-black px-4 py-3 rounded-lg bg-green-white disabled:bg-gray-moose disabled:text-charcoal-gray w-full"
        :placeholder="placeholder"
        :type="inputTypeAttribute"
        :disabled="props.disabled"
        autocomplete="off"
        v-bind="$attrs"
        @blur="handleBlur"
      />
      <button
        v-if="props.showReset"
        type="button"
        class="absolute top-0 right-0 h-full pr-4"
        tabindex="-1"
        @click="onResetInput()"
      >
        <Icon
          name="mdi:close-circle-outline"
          size="24"
          class="stroke-gunmetal"
        />
      </button>

      <button
        v-if="props.passwordToggle"
        type="button"
        class="absolute right-0 top-0 bottom-0 flex items-center px-4"
        tabindex="-1"
        @click="togglePassword()"
      >
        <Icon
          v-show="showPassword"
          name="mdi:eye-off"
          class="text-gray-chateau"
          size="24"
        />
        <Icon
          v-show="!showPassword"
          name="mdi:eye"
          class="text-gray-chateau"
          size="24"
        />
      </button>

      <div v-if="props.loading" class="input-loading">
        <Icon
          name="mdi:loading"
          class="animate-spin text-charcoal-gray"
          size="24"
        />
      </div>

      <div v-if="!props.loading && props.checked" class="input-checked">
        <Icon name="lets-icons:check-fill" class="text-green-600" size="24" />
      </div>
    </div>

    <div class="relative">
      <p
        v-if="newErrorMessage && (meta.touched || props.customErrorMessage)"
        class="absolute top-1 text-indian-red font-normal text-xs"
      >
        {{ newErrorMessage }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.input-loading,
.input-checked {
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  position: absolute;
}
</style>
